import React, { useMemo } from "react";
import { Box, Typography, styled, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import { theme } from "../../../pages/oeeDashboard/styles/theme";

const TimelineWrapper = styled(Box)({
  width: "100%",
  position: "relative",
  marginTop: "10px",
});

const TimelineBar = styled(Box)({
  width: "100%",
  height: "26px",
  background: "#000",
  borderRadius: "4px",
  position: "relative",
  overflow: "visible",
  display: "flex",
});

const Segment = styled(Box)(({ color, width }) => ({
  height: "100%",
  width,
  backgroundColor: color,
  position: "relative",
  cursor: "pointer",
  transition: "all 0.2s ease",

  "&:hover": {
    filter: "brightness(1.25)",
    boxShadow: "0 0 0 2px #ffffff, 0 0 10px rgba(255,255,255,0.6)",
    zIndex: 5,
  },
}));

const Marker = styled("img")(({ left }) => ({
  position: "absolute",
  top: "-18px",
  left,
  width: "49px",
}));

const parseTimeFlexible = (str) => {
  if (!str || str === "NA") return null;

  if (/^\d{1,2}:\d{2}$/.test(str)) {
    return dayjs(`${dayjs().format("YYYY-MM-DD")} ${str}:00`);
  }

  if (/^\d{1,2}:\d{2}:\d{2}$/.test(str)) {
    return dayjs(`${dayjs().format("YYYY-MM-DD")} ${str}`);
  }

  if (/^\d{4}-\d{2}-\d{2} /.test(str)) {
    return dayjs(str);
  }

  return null;
};

// const getMinutesDiffFlexible = (start, end) => {
//   const s = parseTimeFlexible(start);
//   let e = parseTimeFlexible(end);

//   if (!s || !e) return 0;

//   if (e.isBefore(s)) {
//     e = e.add(1, "day");
//   }

//   return e.diff(s, "minute");
// };

// const timeToPercentFlexible = (start, end) =>
//   (getMinutesDiffFlexible(start, end) / (24 * 60)) * 100;

const getSecondsDiffFlexible = (start, end) => {
  const s = parseTimeFlexible(start);
  let e = parseTimeFlexible(end);

  if (!s || !e) return 0;

  if (e.isBefore(s)) {
    e = e.add(1, "day");
  }

  return e.diff(s, "second");
};

const timeToPercentFlexible = (start, end) =>
  (getSecondsDiffFlexible(start, end) / (24 * 60 * 60)) * 100;

const computeLeftPercentFlexible = (start) => {
  const s = parseTimeFlexible(start);
  if (!s) return 0;

  const baseline = dayjs(`${s.format("YYYY-MM-DD")} 06:00:00`);

  let diff = s.diff(baseline, "minute");

  if (diff < 0) {
    diff += 24 * 60;
  }

  return (diff / (24 * 60)) * 100;
};

const mergeContinuousStatus = (segments) => {
  if (!segments.length) return [];

  const merged = [];
  let prev = { ...segments[0] };

  for (let i = 1; i < segments.length; i++) {
    const curr = segments[i];

    const prevEnd = parseTimeFlexible(prev.END);
    const currStart = parseTimeFlexible(curr.START);

    if (!prevEnd || !currStart) continue;

    const sameStatus = prev.STATUS === curr.STATUS;
    const diffMin = currStart.diff(prevEnd, "minute");
    const isContinuous = diffMin >= 0 && diffMin <= 1;

    if (sameStatus && isContinuous) {
      prev.END = curr.END;
      prev.completed = prev.completed || curr.completed;
    } else {
      merged.push(prev);
      prev = { ...curr };
    }
  }

  merged.push(prev);
  return merged;
};

export default function Timeline24H({
  timeline = [],
  flagSrc,
  startTime,
  actualTime,
  endTime,
}) {
  const STATUS_COLOR = {
    Production: "#2EA648",
    Run: "#2EA648",

    "Non-production": "#F1C232",
    Stop: "#F1C232",
    "Change product": "#F1C232",
    Break: "#F1C232",
    Cleaning: "#F1C232",

    Breakdown: "#D62828",
    "Material shortage": "#F1C232",
    "Defect/Reject": "#F1C232",

    IDLE_TIME: "#F1C232",
    Minor: "#e46c0b",
    Other: "#F1C232",
    Rd: "#F1C232",
    Quality: "#F1C232"
  };

  const startDisplay = parseTimeFlexible(startTime)?.format("HH:mm") || "-";
  const actualDisplay = parseTimeFlexible(actualTime)?.format("HH:mm") || "-";
  const endDisplay = parseTimeFlexible(endTime)?.format("HH:mm") || "-";

  // const buildTimelineFullDay = (timeline = [], startTime) => {
  //   const s = parseTimeFlexible(startTime);
  //   if (!s || !timeline.length) return [];

  //   const baseline = dayjs(`${s.format("YYYY-MM-DD")} 06:00:00`);
  //   const endBaseline = baseline.add(1, "day");

  //   const today = dayjs();
  //   const isToday = s.isSame(today, "day");
  //   const targetEnd = isToday ? today : endBaseline;

  //   const sorted = timeline
  //     .map((seg) => ({
  //       ...seg,

  //       STATUS: seg.STATUS || seg.PROCESS || "Non-production",

  //       _start: parseTimeFlexible(seg.START),
  //       _end: parseTimeFlexible(seg.END),
  //     }))
  //     .filter((seg) => seg._start && seg._end)
  //     .sort((a, b) => a._start.diff(b._start));

  //   const result = [];
  //   let cursor = baseline;

  //   sorted.forEach((seg) => {
  //     if (seg._start.isAfter(cursor)) {
  //       result.push({
  //         START: cursor.format("YYYY-MM-DD HH:mm:ss"),
  //         END: seg._start.format("YYYY-MM-DD HH:mm:ss"),
  //         STATUS: "Non-production",
  //       });
  //     }

  //     result.push({
  //       ...seg,
  //       START: seg._start.format("YYYY-MM-DD HH:mm:ss"),
  //       END: seg._end.format("YYYY-MM-DD HH:mm:ss"),
  //     });

  //     cursor = seg._end;
  //   });

  //   if (cursor.isBefore(targetEnd)) {
  //     result.push({
  //       START: cursor.format("YYYY-MM-DD HH:mm:ss"),
  //       END: targetEnd.format("YYYY-MM-DD HH:mm:ss"),
  //       STATUS: "Non-production",
  //     });
  //   }

  //   return result;
  // };
  const buildTimelineFullDay = (timeline = [], startTime) => {
    const s = parseTimeFlexible(startTime);
    if (!s) return [];
  
    let baseline = dayjs(`${s.format("YYYY-MM-DD")} 06:00:00`);

    if (s.hour() < 6) {
      baseline = baseline.subtract(1, "day");
    }
  
    const endBaseline = baseline.add(1, "day");
  
    const now = dayjs();
  
    const isCurrentProductionDay =
      now.isAfter(baseline) && now.isBefore(endBaseline);
  
    const sorted = timeline
      .map((seg) => {
        const segStart = parseTimeFlexible(seg.START);
        const segEnd = parseTimeFlexible(seg.END);
  
        if (!segStart || !segEnd || !segEnd.isAfter(segStart)) return null;
  
        const start = segStart.isBefore(baseline) ? baseline : segStart;
        const end = segEnd.isAfter(endBaseline) ? endBaseline : segEnd;
  
        if (!end.isAfter(start)) return null;
  
        return {
          ...seg,
          STATUS: seg.STATUS || seg.PROCESS || "Non-production",
          _start: start,
          _end: end,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a._start.diff(b._start));
  
    if (sorted.length === 0) return [];
  
    const lastTagEnd = sorted[sorted.length - 1]._end;
    const targetEnd = isCurrentProductionDay ? lastTagEnd : endBaseline;
  
    const result = [];
    let cursor = baseline;
  
    sorted.forEach((seg) => {
      if (seg._start.isAfter(cursor)) {
        result.push({
          START: cursor.format("YYYY-MM-DD HH:mm:ss"),
          END: seg._start.format("YYYY-MM-DD HH:mm:ss"),
          STATUS: "Non-production",
        });
      }
  
      result.push({
        ...seg,
        START: seg._start.format("YYYY-MM-DD HH:mm:ss"),
        END: seg._end.format("YYYY-MM-DD HH:mm:ss"),
      });
  
      if (seg._end.isAfter(cursor)) {
        cursor = seg._end;
      }
    });

    if (cursor.isBefore(targetEnd)) {
      result.push({
        START: cursor.format("YYYY-MM-DD HH:mm:ss"),
        END: targetEnd.format("YYYY-MM-DD HH:mm:ss"),
        STATUS: "Non-production",
      });
    }
  
    return result;
  };

  const displayTimeline = useMemo(() => {
    if (!startTime) return [];

    const full = buildTimelineFullDay(timeline, startTime);
    return mergeContinuousStatus(full);
  }, [timeline, startTime]);

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        sx={{ color: "#CFEDEC" }}
      >
        <Typography fontSize={28}>
          Start{" "}
          <span
            style={{
              color: theme.colorStatus.lightGreen,
              fontSize: 30,
              fontWeight: "bold",
            }}
          >
            {startDisplay}
          </span>
        </Typography>

        <Typography fontSize={28}>
          Actual Start{" "}
          <span
            style={{
              color: theme.colorStatus.lightGreen,
              fontSize: 30,
              fontWeight: "bold",
            }}
          >
            {actualDisplay}
          </span>
        </Typography>

        <Typography fontSize={28}>
          <span
            style={{
              color: theme.colorStatus.gray,
              fontSize: 30,
              fontWeight: "bold",
            }}
          >
            {endDisplay}
          </span>{" "}
          End
        </Typography>
      </Box>

      <TimelineWrapper>
        <TimelineBar>
          {displayTimeline.map((item, idx) => {
            const width = `${timeToPercentFlexible(item.START, item.END)}%`;
            const leftPercent = computeLeftPercentFlexible(item.START);
            const color = STATUS_COLOR[item.STATUS] || "#000";
            return (
              <Tooltip
                key={idx}
                arrow
                title={
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                    <Box>
                      <Typography style={{fontSize: 20}}>
                      {dayjs(item.START).format("HH:mm")} →{" "}
                      {dayjs(item.END).format("HH:mm")} ({item.STATUS})
                      </Typography>
                    </Box>
                    <Box>
                      <Typography style={{fontSize: 20}}>
                      Reason: {item.STATUS_REASON ? item.STATUS_REASON : "-"}
                      </Typography>
                    </Box>
                  </Box>
                }
              >
                <Segment width={width} color={color}>
                  {item.completed && (
                    <Marker
                      src={flagSrc}
                      left={`calc(${leftPercent}% - 15px)`}
                    />
                  )}
                </Segment>
              </Tooltip>
            );
          })}
        </TimelineBar>
      </TimelineWrapper>
    </Box>
  );
}